import React, { useMemo } from 'react';
import { ShowIf } from '../../../../components';
import { PaymentStatus } from 'src/features/common/types/application/payment';
import {
  ApplicationActionType,
  ButtonConfig,
  CatalogueItem,
  DataStatus,
  HelpButton,
  isDeviceSelected,
  isOnlyDeviceSelected,
  LayoutType,
  PageLayout,
  ScrollTop,
  SolutionType,
  useWizardEffect,
} from '../../../common';
import { DEVICE_NAMES } from '../../tech-debt/product-details-extra';
import {
  ButtonParamType,
  DefaultButtonStyle,
} from '../../../common/components/button/revamp-button';
import ButtonGroup from '../../../common/components/button-group/revamp-button-group';
import { SuccessPage } from '../../components/success-page/success-page';
import { FailurePage } from '../../components/failure-page/failure-page';
import { HelpButtonAction, PageName } from '../../services/order-tealium-services';
import { PaymentTimeout } from 'src/features/common/components/payment-status/payment-timeout';
import { MAX_NUMBER_OF_TRIES, publicSiteUrl } from 'src/common';
import { useApplication } from 'src/hooks/useApplication';
import { getApplicationAPIFromApplication } from 'src/features/common/helpers/dto-mappers/salesforce/get-application-api-from-application';
import { useDeepCompareEffect } from 'src/hooks/useDeepCompareEffect';
import { Environment } from 'src/Environment';
import './order-post-submit-result.scss';

export function OrderPostSubmitResult() {
  const {
    application,
    dispatch,
    catalogue,
    salesforceService,
    dpoTealiumService,
    orderTealiumService,
  } = useApplication();
  const isPaymentRequired = (catalogue.totals.onceOff || 0) > 0;
  const paymentFailurePageName = PageName.PAYMENT_FAILURE;
  const paymentSuccessPageName = PageName.ORDER_COMPLETE;
  const isPaymentSuccessful =
    application?.payment?.paymentStatus === PaymentStatus.PAYMENT_SUCCESSFUL ||
    (application.solutionType === SolutionType.thirdPartyOnboarding &&
      !application.payment?.onlinePaymentRequired);

  const eCommerceOnlySelected = useMemo(
    () => isOnlyDeviceSelected(catalogue, DEVICE_NAMES.VODAPAY_ECOM_GATEWAY),
    [catalogue.products],
  );
  const kwikaOrMaxSelected = useMemo(
    () => isDeviceSelected(catalogue, [DEVICE_NAMES.VODAPAY_KWIKA, DEVICE_NAMES.VODAPAY_MAX]),
    [catalogue?.products],
  );
  const maxNumberOfTries =
    (application.remotePayment?.numberOfTries || application.gatewayPayment?.numberOfTries || 0) >=
    (application.payment?.maxNumberOfTries ?? MAX_NUMBER_OF_TRIES);

  const errorMessage = useMemo(() => {
    if (application.gatewayPayment?.responseMessage || application.remotePayment?.responseMessage) {
      return maxNumberOfTries
        ? 'Please contact your bank for more information'
        : 'Please try again or contact your bank for more information';
    }
  }, [application]);

  const saveApp = async () => {
    await salesforceService.saveApplication(
      getApplicationAPIFromApplication(application, catalogue),
      true,
    );
  };

  useWizardEffect(() => {
    return {
      nextButtonVisible: false,
      backButtonVisible: false,
      footerVisible: false,
      nextButtonEnabled: true,
      onNextButtonClicked: () => {
        if (!isPaymentRequired || isPaymentSuccessful) {
          dispatch({ type: ApplicationActionType.NAVIGATE_TO_NEXT_STEP });
        }
      },
      navigateBackEnabled: isPaymentRequired && !isPaymentSuccessful,
      hideProgressBar: application.solutionType === SolutionType.buyInStoreFicaOnline,
    };
  }, [application, isPaymentSuccessful, isPaymentRequired]);

  useDeepCompareEffect(() => {
    if (application.gatewayPayment?.initiated && catalogue.statusAll === DataStatus.LOADED) {
      // Send response code and message to SF
      saveApp();
    }
  }, [application, catalogue.statusAll]);

  const buttonContinue: ButtonConfig = {
    automationId: 'Revamp_Order_Outcome_Get_Started_Button',
    label: kwikaOrMaxSelected ? 'Create my profile now' : 'Setup my account',
    buttonStyle: DefaultButtonStyle().hasPrimaryAction().isLargeSize().hasLightMode().build(),
    className: 'continue-button',
    buttonParams: {
      type: ButtonParamType.NormalButton,
      onClick: () => {
        orderTealiumService?.onOrderConfirmEnd();
        dispatch({ type: ApplicationActionType.NAVIGATE_TO_NEXT_STEP });
      },
    },
  };

  const onAnalyticsSuccessfulPageStart = () => {
    const catalogueValues = {
      ...catalogue,
      products: catalogue.products
        .filter(
          (product) => !product.device.productCategoryName || product.device.isProductCategory,
        )
        .filter(({ device }) => device.quantity > 0),
    };

    let productList: CatalogueItem[] = catalogueValues.products;

    Environment.current.disabledFeatures.dpo
      ? orderTealiumService?.onOrderCompletePageStart(productList, application.referenceNumber)
      : dpoTealiumService?.onSuccessPageView(application.cart || [], application.referenceNumber!);
  };

  const onPaymentFailureRetry = () => {
    orderTealiumService?.onPaymentFailureAction();
    if (application.gatewayPayment?.initiated) {
      dispatch({
        type: ApplicationActionType.MULTIPLE_ACTIONS,
        actions: [
          { type: ApplicationActionType.RESET_GATEWAY_PAYMENT_STATUS },
          { type: ApplicationActionType.NAVIGATE_TO_PREVIOUS_STEP },
        ],
      });
      return;
    }
    dispatch({
      type: ApplicationActionType.MULTIPLE_ACTIONS,
      actions: [
        { type: ApplicationActionType.RESET_REMOTE_PAYMENT_STATUS },
        { type: ApplicationActionType.NAVIGATE_TO_PREVIOUS_STEP },
      ],
    });
  };

  if (application.payment?.paymentStatus === PaymentStatus.TIMEOUT || maxNumberOfTries) {
    return (
      <PaymentTimeout
        maxNumberOfTries={maxNumberOfTries}
        subStatus={errorMessage}
        onClick={() => {
          if (maxNumberOfTries) {
            window.location.href = publicSiteUrl;
            return;
          }

          dispatch({
            type: ApplicationActionType.MULTIPLE_ACTIONS,
            actions: [
              { type: ApplicationActionType.RESET_REMOTE_PAYMENT_STATUS },
              { type: ApplicationActionType.NAVIGATE_TO_PREVIOUS_STEP },
            ],
          });
        }}
      />
    );
  }

  return (
    <section className="OrderPostSubmitResult">
      <PageLayout>
        <ScrollTop />
        <ShowIf condition={!isPaymentRequired || isPaymentSuccessful}>
          <SuccessPage
            onTealiumPageStart={() => onAnalyticsSuccessfulPageStart()}
            eCommerceOnlySelected={eCommerceOnlySelected}
            contactName={application.contact?.name}
            paymentGateway={application.gatewayPayment?.initiated}
          />
          <ButtonGroup buttons={[buttonContinue]} layoutType={LayoutType.CENTERED} />
          <HelpButton
            onOpen={() =>
              orderTealiumService?.onHelpButtonActions(
                paymentSuccessPageName,
                HelpButtonAction.CLICK,
              )
            }
            onEmailClicked={() =>
              orderTealiumService?.onHelpButtonActions(
                paymentSuccessPageName,
                HelpButtonAction.EMAIL,
              )
            }
            onPhoneNumberClicked={() =>
              orderTealiumService?.onHelpButtonActions(
                paymentSuccessPageName,
                HelpButtonAction.CALL,
              )
            }
          />
        </ShowIf>

        <ShowIf condition={isPaymentRequired && !isPaymentSuccessful}>
          <FailurePage
            onFailurePageShow={() => orderTealiumService?.onPaymentFailureStart()}
            onFailurePageDispatch={() => onPaymentFailureRetry()}
            errorMessage={errorMessage}
          />
          <HelpButton
            onOpen={() =>
              orderTealiumService?.onHelpButtonActions(
                paymentFailurePageName,
                HelpButtonAction.CLICK,
              )
            }
            onEmailClicked={() =>
              orderTealiumService?.onHelpButtonActions(
                paymentFailurePageName,
                HelpButtonAction.EMAIL,
              )
            }
            onPhoneNumberClicked={() =>
              orderTealiumService?.onHelpButtonActions(
                paymentFailurePageName,
                HelpButtonAction.CALL,
              )
            }
          />
        </ShowIf>
      </PageLayout>
    </section>
  );
}
